import React, { createContext, useContext, useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase, setupSlackUserAndTeam } from '../lib/supabase'
import { Tables, TeamRole } from '../types/supabase'

// Define the proper type for user teams with joined team data
type UserTeamsData = (Tables<'team_members'> & {
  teams: {
    id: string
    name: string | null
    plan: string | null
    slack_team_id: string
    created_at: string | null
    // pending_review/approved/rejected -- see
    // 20260917120000_add_pending_review_status_to_teams.sql. Checked by
    // TeamProtectedRoute to block dashboard access for unapproved workspaces.
    status: string | null
  }
})[]

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  userTeams: UserTeamsData | null
  userInvitations: any[] | null
  refreshUserTeams: () => Promise<void>
  refreshInvitations: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [userTeams, setUserTeams] = useState<UserTeamsData | null>(null)
  const [userInvitations, setUserInvitations] = useState<any[] | null>(null)
  
  // Track the last time we loaded data to prevent excessive reloading
  const lastDataLoad = useRef<number>(0)
  const DATA_LOAD_COOLDOWN = 3000 // 3 seconds between data loads
  
  // Track if we've already set up Slack for this user session
  const slackSetupCompleted = useRef<boolean>(false)
  const currentUserId = useRef<string | null>(null)

  const loadUserData = useCallback(async (currentUser: User, forceLoad = false) => {
    if (!currentUser.email) {
      // console.log('No user email, skipping data load')
      return
    }

    // Check cooldown to prevent excessive loading (unless forced)
    const now = Date.now()
    if (!forceLoad && now - lastDataLoad.current < DATA_LOAD_COOLDOWN) {
      // console.log('Data load cooldown active, skipping')
      return
    }

    try {
      // console.log('Loading user data for:', currentUser.email)
      // console.log('User ID:', currentUser.id)
      lastDataLoad.current = now

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Data load timeout after 8 seconds')), 8000)
      })

      // console.log('Starting teams query...')
      const teamsPromise = supabase
        .from('team_members')
        .select(`
          *,
          teams:teams(id, name, plan, slack_team_id, created_at, status)
        `)
        .eq('auth_user_id', currentUser.id)
        .order('joined_at', { ascending: false })

      // console.log('Starting invitations query...')
      const invitationsPromise = supabase
        .from('team_invitations')
        .select(`
          *,
          teams:teams(id, name)
        `)
        .eq('email', currentUser.email)
        .is('accepted_at', null)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })

      // console.log('Waiting for both queries to complete...')
      
      // Race the data loading against the timeout
      const [teamsResult, invitationsResult] = await Promise.race([
        Promise.all([teamsPromise, invitationsPromise]),
        timeoutPromise
      ]) as any

      // console.log('Queries completed, processing results...')

      if (teamsResult.error) {
        console.error('Error loading teams:', teamsResult.error)
      } else {
        const processedTeams = teamsResult.data?.map((membership: any) => ({
          ...membership,
          teams: membership.teams || null
        })) || []
        setUserTeams(processedTeams as UserTeamsData)
        // console.log('Teams loaded:', processedTeams.length)
      }

      if (invitationsResult.error) {
        console.error('Error loading invitations:', invitationsResult.error)
      } else {
        const processedInvitations = invitationsResult.data?.map((invitation: any) => ({
          ...invitation,
          teams: invitation.teams || null
        })) || []
        setUserInvitations(processedInvitations)
        // console.log('Invitations loaded:', processedInvitations.length)
      }

      // console.log('User data loading completed successfully')

    } catch (error) {
      console.error('Error loading user data:', error)
      
      // If we timeout or error, try to load data separately to isolate the issue
      // console.log('Attempting to load data separately due to error...')
      
      try {
        // console.log('Loading teams only...')
        const { data: teams, error: teamsError } = await supabase
          .from('team_members')
          .select(`
            *,
            teams:teams(id, name, plan, slack_team_id, created_at, status)
          `)
          .eq('auth_user_id', currentUser.id)
          .order('joined_at', { ascending: false })
        
        if (teamsError) {
          console.error('Teams query error:', teamsError)
        } else {
          const processedTeams = teams?.map(membership => ({
            ...membership,
            teams: membership.teams || null
          })) || []
          setUserTeams(processedTeams as UserTeamsData)
          // console.log('Teams loaded separately:', processedTeams.length)
        }
      } catch (teamsErr) {
        console.error('Failed to load teams separately:', teamsErr)
        setUserTeams([]) // Set empty array instead of null
      }

      try {
        // console.log('Loading invitations only...')
        const { data: invitations, error: invitationsError } = await supabase
          .from('team_invitations')
          .select(`
            *,
            teams:teams(id, name)
          `)
          .eq('email', currentUser.email)
          .is('accepted_at', null)
          .gt('expires_at', new Date().toISOString())
          .order('created_at', { ascending: false })
        
        if (invitationsError) {
          console.error('Invitations query error:', invitationsError)
        } else {
          const processedInvitations = invitations?.map(invitation => ({
            ...invitation,
            teams: invitation.teams || null
          })) || []
          setUserInvitations(processedInvitations)
          // console.log('Invitations loaded separately:', processedInvitations.length)
        }
      } catch (invitationsErr) {
        console.error('Failed to load invitations separately:', invitationsErr)
        setUserInvitations([]) // Set empty array instead of null
      }
    }
  }, [])

  const refreshUserTeams = useCallback(async () => {
    if (!user) return
    try {
      // console.log('Refreshing user teams')
      const { data: teams, error } = await supabase
        .from('team_members')
        .select(`
          *,
          teams:teams(id, name, plan, slack_team_id, created_at, status)
        `)
        .eq('auth_user_id', user.id)
        .order('joined_at', { ascending: false })
      
      if (error) throw error
      
      const processedTeams = teams?.map(membership => ({
        ...membership,
        teams: membership.teams || null
      })) || []
      
      setUserTeams(processedTeams as UserTeamsData)
    } catch (error) {
      console.error('Error refreshing user teams:', error)
    }
  }, [user])

  const refreshInvitations = useCallback(async () => {
    if (!user?.email) return
    try {
      // console.log('Refreshing invitations')
      const { data: invitations, error } = await supabase
        .from('team_invitations')
        .select(`
          *,
          teams:teams(id, name)
        `)
        .eq('email', user.email)
        .is('accepted_at', null)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })
      
      if (error) throw error
      
      const processedInvitations = invitations?.map(invitation => ({
        ...invitation,
        teams: invitation.teams || null
      })) || []
      
      setUserInvitations(processedInvitations)
    } catch (error) {
      console.error('Error refreshing invitations:', error)
    }
  }, [user])

  const handleSignOut = useCallback(async () => {
    try {
      await supabase.auth.signOut()
      setUser(null)
      setSession(null)
      setUserTeams(null)
      setUserInvitations(null)
      // Reset Slack setup tracking
      slackSetupCompleted.current = false
      currentUserId.current = null
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }, [])

  useEffect(() => {
    let mounted = true

    // Check for OAuth callback in URL hash first
    const checkForOAuthCallback = async () => {
      const url = new URL(window.location.href)
      const hashParams = new URLSearchParams(url.hash.substring(1))
      
      // Check if we have auth tokens in the hash
      if (hashParams.has('access_token') && hashParams.has('refresh_token')) {
        // console.log('OAuth callback detected, processing tokens...')
        
        try {
          const accessToken = hashParams.get('access_token')
          const refreshToken = hashParams.get('refresh_token')
          
          if (accessToken && refreshToken) {
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            })
            
            if (error) {
              console.error('Failed to set session from OAuth callback:', error)
              return false
            } else {
              // console.log('OAuth session established successfully:', data.user?.email)
              
              if (mounted) {
                setSession(data.session)
                setUser(data.user)
                setLoading(false)
                
                // Clear the hash from URL
                window.history.replaceState(null, '', window.location.pathname + window.location.search)
                
                // Set up Slack user and team for new OAuth login
                if (data.user) {
                  // Check if this is a new Slack OIDC login and set up team
                  const isSlackOIDC = data.user.app_metadata?.provider === 'slack_oidc' || 
                                     data.user.user_metadata?.iss?.includes('slack.com') ||
                                     data.user.user_metadata?.custom_claims?.['https://slack.com/team_id']
                  const isNewUser = currentUserId.current !== data.user.id
                  
                  currentUserId.current = data.user.id
                  
                  if (isSlackOIDC && !slackSetupCompleted.current) {
                    console.log('Detected Slack OIDC login, setting up user and team...')
                    slackSetupCompleted.current = true // Set immediately to prevent duplicate calls
                    
                    try {
                      // Extract plan from URL if present
                      const urlParams = new URLSearchParams(window.location.search)
                      let planFromUrl = urlParams.get('upgrade') || urlParams.get('plan')
                      
                      // Check localStorage for pending plan (stored before OAuth redirect)
                      if (!planFromUrl) {
                        planFromUrl = localStorage.getItem('pending_plan')
                        if (planFromUrl) {
                          // Clean up localStorage
                          localStorage.removeItem('pending_plan')
                        }
                      }
                      
                      console.log('Setting up Slack user and team with plan:', planFromUrl)
                      await setupSlackUserAndTeam(planFromUrl || undefined, hashParams.get('provider_token') || data.session?.provider_token)
                      console.log('Slack setup completed successfully')
                      
                      // Force reload data after successful setup
                      lastDataLoad.current = 0
                      await loadUserData(data.user, true)
                    } catch (setupError) {
                      console.error('Failed to setup Slack user and team:', setupError)
                      slackSetupCompleted.current = false // Reset on error to allow retry
                    }
                  } else {
                    // Not a new Slack login, just load data normally
                    loadUserData(data.user, true).catch(error => {
                      console.error('Background data loading failed after OAuth:', error)
                    })
                  }
                }
              }
              
              return true // Successfully processed OAuth callback
            }
          }
        } catch (error) {
          console.error('Error processing OAuth callback:', error)
          return false
        }
      }
      
      return false // No OAuth callback found
    }

    // Get initial session
    const initializeAuth = async () => {
      try {
        // console.log('Initializing auth...')
        
        // First check for OAuth callback in URL
        const processedOAuth = await checkForOAuthCallback()
        if (processedOAuth) {
          // console.log('OAuth callback processed, skipping regular session check')
          return
        }
        
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (error) {
          console.error('Error getting session:', error)
          if (mounted) setLoading(false)
          return
        }

        console.log('Session retrieved:', session?.user?.email || 'No session')
        
        if (mounted) {
          setSession(session)
          setUser(session?.user ?? null)
          
          // Set loading to false immediately so UI can render
          setLoading(false)
          
          // Track user change for Slack setup
          if (session?.user) {
            currentUserId.current = session.user.id
            
            // Check if this is a Slack user that needs setup
            const isSlackUser = session.user.app_metadata?.provider === 'slack_oidc' || 
                               session.user.user_metadata?.iss?.includes('slack.com') ||
                               session.user.user_metadata?.custom_claims?.['https://slack.com/team_id']
            
            if (isSlackUser && !slackSetupCompleted.current) {
              // console.log('Detected Slack user needing setup, checking if teams exist...')
              
              // Check if user has teams in database first
              supabase
                .from('team_members')
                .select('id')
                .eq('auth_user_id', session.user.id)
                .limit(1)
                .then(({ data: existingTeams, error: teamCheckError }) => {
                  if (teamCheckError) {
                    // console.log('Error checking existing teams (expected if user not set up):', teamCheckError)
                  }
                  
                  if (!existingTeams || existingTeams.length === 0) {
                    // console.log('Slack user has no teams, running setup...')
                    slackSetupCompleted.current = true
                    
                    setupSlackUserAndTeam(undefined, session.provider_token).then(() => {
                      // console.log('Slack setup completed for existing session')
                      // Force reload data after successful setup
                      lastDataLoad.current = 0
                      loadUserData(session.user, true)
                    }).catch(setupError => {
                      console.error('Failed to setup Slack user and team for existing session:', setupError)
                      slackSetupCompleted.current = false
                    })
                  } else {
                    // console.log('Slack user already has teams, skipping setup')
                    // Just load user data normally
                    loadUserData(session.user).catch(error => {
                      console.error('Background data loading failed:', error)
                    })
                  }
                })
            } else {
              // Load user data in the background if we have a user
              // Don't await this - let it load in background
              loadUserData(session.user).catch(error => {
                console.error('Background data loading failed:', error)
              })
            }
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error)
        if (mounted) setLoading(false)
      }
    }

    initializeAuth()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // console.log('Auth state changed:', event, session?.user?.email)
        
        if (!mounted) return

        // Update session and user state
        setSession(session)
        setUser(session?.user ?? null)

        // Handle different auth events
        if (event === 'SIGNED_IN' && session) {
          // Just load user data - Slack setup is handled in OAuth callback
          loadUserData(session.user).catch(error => {
            console.error('Data loading failed for SIGNED_IN:', error)
          })
        } else if (event === 'SIGNED_OUT') {
          // User signed out, clear data
          setUserTeams(null)
          setUserInvitations(null)
          slackSetupCompleted.current = false
          currentUserId.current = null
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          // Token refreshed (happens on tab focus), only reload if we don't have data
          if (!userTeams) {
            // console.log('Token refreshed and no teams data, reloading...')
            loadUserData(session.user).catch(error => {
              console.error('Data loading failed for TOKEN_REFRESHED:', error)
            })
          }
        }
        
        // Make sure loading is false after handling auth changes
        setLoading(false)
      }
    )

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [loadUserData, userTeams]) // Include userTeams in deps for TOKEN_REFRESHED logic

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo<AuthContextType>(() => ({
    user,
    session,
    loading,
    userTeams,
    userInvitations,
    refreshUserTeams,
    refreshInvitations,
    signOut: handleSignOut,
  }), [user, session, loading, userTeams, userInvitations, refreshUserTeams, refreshInvitations, handleSignOut])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Hook to check if user has access to a specific team
export const useTeamAccess = (teamId: string | undefined) => {
  const { userTeams, user } = useAuth()
  const [teamAccess, setTeamAccess] = useState<{
    hasAccess: boolean
    role?: TeamRole
    loading: boolean
  }>({ hasAccess: false, loading: true })

  useEffect(() => {
    if (!teamId || !user) {
      setTeamAccess({ hasAccess: false, loading: false })
      return
    }

    if (!userTeams) {
      setTeamAccess({ hasAccess: false, loading: true })
      return
    }

    const membership = userTeams.find(membership => membership.team_id === teamId)
    
    setTeamAccess({
      hasAccess: !!membership,
      role: membership?.role as TeamRole,
      loading: false
    })
  }, [teamId, user, userTeams])

  return teamAccess
}
